// Cmd+K Command Palette — Story 9 (search) + Theme K3 (action runner).
//
// When the query is EMPTY the palette shows a curated list of ACTIONS
// (navigation + create + help). As the user types, actions are filtered by
// fuzzy subsequence match and merged above the live server search results.
//
// Sections:
//   "Aktionen"  — fuzzy-filtered action rows (empty-query: show all)
//   "Projekte"  — server search hits of type "project"
//   "Aufgaben"  — server search hits of type "task"
//
// Keyboard: ArrowUp / ArrowDown / Enter navigate the flat merged list.
// Esc closes the palette. Mouse hover also moves the highlight.
//
// Backend: GET /api/search?q=… returns { results: SearchResult[] } sorted
// projects-first then tasks. Server search is skipped when query is empty.
//
// New-project flow: running the "New project" action calls the mandatory
// `onCreateProject` prop. App.tsx passes a callback that opens its own
// <NewProjectModal> (same pattern as Sidebar.tsx / routes/Projects.tsx),
// keeping the modal lifecycle in the router root without a second, unread
// signal.
//
// Navigation actions: generated from `paletteTargets()` (lib/navigation.ts,
// the single navigation source) — not a hand-maintained list. Only the
// non-navigation actions (create, help) are authored here.

import { createPortal } from "preact/compat";
import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import { useLocation } from "wouter-preact";
import {
  CheckSquare,
  Folder,
  FolderPlus,
  Keyboard,
  ListChecks,
  ListTodo,
  PencilLine,
  Search,
  Sparkles,
  type LucideIcon,
} from "lucide-preact";
import { signal } from "@preact/signals";
import { openProjectTaskSig, openPersonalTaskSig } from "../store/deepLinks";
import { api } from "../lib/api";
import { fuzzyFilter } from "../lib/fuzzy";
import { paletteTargets } from "../lib/navigation";
import { uxEnabled } from "../lib/ux";
import { openCapture } from "../store/capture";
import { openTaskImport } from "../store/taskImport";
import { useMe } from "../lib/auth";

// ---------------------------------------------------------------------------
// Module-level signals — shared across App + Sidebar without prop drilling.
// ---------------------------------------------------------------------------

/** Controls whether the palette is open. */
export const paletteOpen = signal(false);
/**
 * Pre-fills the palette's search query on open, so a single action is
 * preselected (e.g. the `c` hotkey opens the palette filtered down to
 * "Neues Projekt"). `undefined` = opens empty, as with Cmd+K.
 */
export const paletteInitialQuery = signal<string | undefined>(undefined);
export const openPalette = (initialQuery?: string): void => {
  paletteInitialQuery.value = initialQuery;
  paletteOpen.value = true;
};
export const closePalette = (): void => {
  paletteOpen.value = false;
  paletteInitialQuery.value = undefined;
};

// Deep-link signals now live in a leaf module (../store/deepLinks) so light-weight
// consumers (e.g. a task row) can reach them without importing this whole
// component. Imported for this component's own use and re-exported for the
// existing call sites that reference them via CommandPalette.
export { openProjectTaskSig, openPersonalTaskSig };

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type SearchResultType = "project" | "task" | "list" | "personal_task";

interface SearchResult {
  type: SearchResultType;
  id: number;
  title: string;
  subtitle?: string;
  project_id?: number;
  color?: string;
  /** "comment" = matched in a task comment, "note" = in a task's notes. */
  matched_via?: "comment" | "note";
}

/** Type filter chip → backend `type` param (empty = all types). */
type TypeFilter = "all" | "project" | "task" | "list";
const TYPE_PARAM: Record<TypeFilter, string> = {
  all: "",
  project: "project",
  task: "task,personal_task",
  list: "list",
};
const TYPE_LABELS: { value: TypeFilter; label: string }[] = [
  { value: "all", label: "Alle" },
  { value: "project", label: "Projekte" },
  { value: "task", label: "Aufgaben" },
  { value: "list", label: "Listen" },
];

type StatusFilter = "all" | "open" | "done";
const STATUS_LABELS: { value: StatusFilter; label: string }[] = [
  { value: "all", label: "Alle" },
  { value: "open", label: "Offen" },
  { value: "done", label: "Erledigt" },
];

/** An action shown by the palette. */
export interface PaletteAction {
  id: string;
  label: string;
  /** Extra words that the fuzzy filter also considers. */
  keywords?: string;
  /** Lucide icon component (size injected by palette). */
  icon: LucideIcon;
  run: () => void;
}

interface CommandPaletteProps {
  onClose: () => void;
  /** Injected by App.tsx so the palette can open the HotkeyHelp overlay. */
  onShowHelp: () => void;
  /** Injected by App.tsx so the palette can open the "Neues Projekt" flow. */
  onCreateProject: () => void;
  /** Pre-fills the search query (see `paletteInitialQuery`). */
  initialQuery?: string;
}

// ---------------------------------------------------------------------------
// Merged list item — either a PaletteAction or a SearchResult.
// ---------------------------------------------------------------------------

type ListItem =
  | { kind: "action"; action: PaletteAction }
  | { kind: "result"; result: SearchResult };

// ---------------------------------------------------------------------------
// CommandPalette component
// ---------------------------------------------------------------------------

export function CommandPalette({
  onClose,
  onShowHelp,
  onCreateProject,
  initialQuery,
}: CommandPaletteProps): preact.JSX.Element {
  const [query, setQuery] = useState(initialQuery ?? "");
  const [debounced, setDebounced] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  // Serversuche fehlgeschlagen. Bewusst KEIN Toast: die Suche feuert pro
  // Tastenanschlag, eine Meldung je Fehlschlag waere unerträglich — deshalb
  // laeuft der Abruf mit `silentError: true`. Der Fehler darf aber nicht wie
  // „nichts gefunden" aussehen: sonst glaubt der Nutzer, seine Aufgabe gaebe
  // es nicht, obwohl der Server nur nicht geantwortet hat.
  const [searchFailed, setSearchFailed] = useState(false);
  const [typeFilter, setTypeFilter] = useState<TypeFilter>("all");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [highlight, setHighlight] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const [, setLocation] = useLocation();

  // ------------------------------------------------------------------
  // Build the action list. These are stable (no per-render allocations)
  // but reference setLocation / onClose / onShowHelp / onCreateProject via
  // closure so they must live inside the component.
  //
  // Navigation actions are GENERATED from `paletteTargets()` (the single
  // navigation source, lib/navigation.ts) — keywords come from the target's
  // label plus its `paletteAliases` (old names like "Mein Tag" stay
  // searchable). Only the non-navigation actions (create, help) are
  // hand-authored below.
  // ------------------------------------------------------------------
  // UX-Vorschlag P2: mit Flag gibt es „Erfassen" (globale Schnellerfassung)
  // ganz oben, und „Neue Aufgabe" führt ebenfalls dorthin statt nach /lists.
  const captureOn = uxEnabled("ux_web_p2");
  const me = useMe();
  const importOn = me.user?.ai_enabled === true && !me.user.is_external;
  const actions = useMemo<PaletteAction[]>(
    () => [
      ...(captureOn
        ? [
            {
              id: "create:capture",
              label: "Erfassen",
              keywords: "erfassen schnellerfassung aufgabe neu task capture n",
              icon: PencilLine,
              run: () => { onClose(); openCapture(); },
            } satisfies PaletteAction,
          ]
        : []),
      ...paletteTargets().map(
        (t): PaletteAction => ({
          id: `nav:${t.id}`,
          label: t.label,
          keywords: [t.label, ...(t.paletteAliases ?? [])].join(" "),
          icon: t.icon,
          run: () => { setLocation(t.path); onClose(); },
        })
      ),
      {
        id: "create:project",
        label: "Neues Projekt",
        keywords: "new project erstellen anlegen",
        icon: FolderPlus,
        run: () => { onCreateProject(); onClose(); },
      },
      {
        id: "create:personal-task",
        label: "Neue Aufgabe",
        keywords: "aufgabe task todo persönlich liste",
        icon: ListTodo,
        run: captureOn
          ? () => { onClose(); openCapture(); }
          : () => { setLocation("/lists"); onClose(); },
      },
      ...(importOn
        ? [
            {
              id: "create:task-import",
              label: "Aufgabenliste importieren (KI)",
              keywords: "liste import einfügen paste ki ai mehrere aufgaben projekt füllen",
              icon: Sparkles,
              run: () => { onClose(); openTaskImport(); },
            },
          ]
        : []),
      {
        id: "help:shortcuts",
        label: "Tastatur-Shortcuts",
        keywords: "keyboard shortcuts help hilfe hotkeys",
        icon: Keyboard,
        run: () => { onShowHelp(); onClose(); },
      },
    ],
    // setLocation reference is stable across re-renders; onClose,
    // onShowHelp and onCreateProject are passed down from App and are
    // stable callbacks.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onClose, onShowHelp, onCreateProject, captureOn, importOn]
  );

  // ------------------------------------------------------------------
  // Filtered actions — show all when query is empty, fuzzy-filter otherwise.
  // ------------------------------------------------------------------
  const filteredActions = useMemo<PaletteAction[]>(() => {
    if (!query.trim()) return actions;
    return fuzzyFilter(actions, query.trim(), (a) => `${a.label} ${a.keywords ?? ""}`);
  }, [actions, query]);

  // ------------------------------------------------------------------
  // Focus + body-scroll-lock on mount; restore focus to the trigger on close
  // (same pattern as Modal.tsx) so keyboard users aren't dumped on <body>.
  // ------------------------------------------------------------------
  useEffect(() => {
    const prevFocused = document.activeElement as HTMLElement | null;
    inputRef.current?.focus();
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
      if (prevFocused && typeof prevFocused.focus === "function" && document.contains(prevFocused)) {
        prevFocused.focus();
      }
    };
  }, []);

  // ------------------------------------------------------------------
  // Inline debounce — 200 ms after the last keystroke.
  // ------------------------------------------------------------------
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(query.trim()), 200);
    return () => window.clearTimeout(t);
  }, [query]);

  // ------------------------------------------------------------------
  // Fetch server results on debounced query change.
  // ------------------------------------------------------------------
  useEffect(() => {
    if (!debounced) {
      setResults([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const params = new URLSearchParams({ q: debounced });
    if (statusFilter !== "all") params.set("status", statusFilter);
    if (TYPE_PARAM[typeFilter]) params.set("type", TYPE_PARAM[typeFilter]);
    const url = `/api/search?${params.toString()}`;
    void api<{ results: SearchResult[] }>(url, { silentError: true })
      .then((data) => {
        if (cancelled) return;
        setResults(data.results ?? []);
        setHighlight(0);
        setLoading(false);
        setSearchFailed(false);
      })
      .catch(() => {
        if (cancelled) return;
        setResults([]);
        setLoading(false);
        setSearchFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [debounced, statusFilter, typeFilter]);

  // ------------------------------------------------------------------
  // Merged flat list — actions first, then server results.
  // ------------------------------------------------------------------
  const items = useMemo<ListItem[]>(() => {
    const actionItems: ListItem[] = filteredActions.map((a) => ({ kind: "action", action: a }));
    const resultItems: ListItem[] = results.map((r) => ({ kind: "result", result: r }));
    return [...actionItems, ...resultItems];
  }, [filteredActions, results]);

  // Reset highlight when the merged list changes length.
  useEffect(() => {
    setHighlight(0);
  }, [items.length]);

  // ------------------------------------------------------------------
  // Navigate to a search result.
  // ------------------------------------------------------------------
  const navigateToResult = useMemo(
    () => (r: SearchResult): void => {
      if (r.type === "task" && r.project_id) {
        // Signal opens the task detail once Project has loaded — reliable even
        // when already viewing this project (see openProjectTaskSig docs).
        openProjectTaskSig.value = r.id;
        setLocation(`/p/${r.project_id}`);
      } else if (r.type === "project") {
        setLocation(`/p/${r.id}`);
      } else if (r.type === "list") {
        setLocation(`/lists/${r.id}`);
      } else if (r.type === "personal_task") {
        // Signal opens the detail panel regardless of the active list.
        openPersonalTaskSig.value = r.id;
        setLocation(`/lists`);
      }
      onClose();
    },
    [setLocation, onClose]
  );

  // ------------------------------------------------------------------
  // Keyboard nav: arrows + enter + esc.
  // ------------------------------------------------------------------
  const onKeyDown = (e: KeyboardEvent): void => {
    if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      onClose();
      return;
    }
    if (items.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => (h + 1) % items.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => (h - 1 + items.length) % items.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      const item = items[highlight];
      if (!item) return;
      if (item.kind === "action") {
        item.action.run();
      } else {
        navigateToResult(item.result);
      }
    }
  };

  // Keep the highlighted row in view.
  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const node = list.querySelector<HTMLElement>(`[data-idx="${highlight}"]`);
    node?.scrollIntoView({ block: "nearest" });
  }, [highlight]);

  // ------------------------------------------------------------------
  // Section boundary helpers (for rendering section headers).
  // ------------------------------------------------------------------
  const firstResultIdx = useMemo(
    () => items.findIndex((it) => it.kind === "result"),
    [items]
  );

  // ------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------
  const showEmpty = !query.trim() && items.length === 0;
  const showNoMatch =
    query.trim() !== "" && !loading && items.length === 0 && !searchFailed;
  const showSearchFailed =
    query.trim() !== "" && !loading && items.length === 0 && searchFailed;

  const node = (
    <div
      class="fixed inset-0 z-50 bg-[color:var(--color-on-surface)]/40 backdrop-blur-sm"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      role="presentation"
    >
      <div
        class="fixed left-1/2 -translate-x-1/2 w-[calc(100vw-2rem)] max-w-[560px] rounded-lg bg-[color:var(--color-surface-container-lowest)] shadow-level-3 border border-[color:var(--color-outline-variant)] flex flex-col overflow-hidden focus:outline-none"
        style={{ top: "15vh", maxHeight: "70vh" }}
        role="dialog"
        aria-modal="true"
        aria-label="Befehlspalette"
        onKeyDown={onKeyDown}
      >
        {/* Input */}
        {/* Fokus zeigt die ganze Suchzeile (Unterkante in Akzentfarbe) statt
            eines Rechtecks um das randlose Feld; `focus-visible:ring-0` am
            Feld nimmt es aus dem globalen Fokus-Sicherheitsnetz (index.css). */}
        <div class="flex items-center gap-2 px-4 py-3 border-b border-[color:var(--color-outline-variant)] focus-within:border-[color:var(--color-primary)]">
          <Search size={16} class="text-[color:var(--color-on-surface-variant)] shrink-0" aria-hidden="true" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onInput={(e) => setQuery((e.target as HTMLInputElement).value)}
            placeholder="Suche oder Aktion eingeben…"
            class="flex-1 min-w-0 bg-transparent text-base text-[color:var(--color-on-surface)] placeholder:text-[color:var(--color-on-surface-variant)] focus:outline-none focus-visible:ring-0"
            aria-label="Befehl oder Suchbegriff"
            autoComplete="off"
            spellcheck={false}
            role="combobox"
            aria-expanded={items.length > 0}
            aria-controls="juuplan-cmdk-list"
            aria-activedescendant={items.length > 0 ? `juuplan-cmdk-opt-${highlight}` : undefined}
          />
          {loading && (
            <span class="text-label uppercase tracking-wider text-[color:var(--color-on-surface-variant)]">
              Lädt…
            </span>
          )}
        </div>

        {/* Filters — type + completion. Only meaningful (and shown) while
            searching; hidden in the empty-query action-launcher state. */}
        {query.trim() !== "" && (
          <div class="flex flex-wrap items-center gap-x-4 gap-y-1.5 px-4 py-2 border-b border-[color:var(--color-outline-variant)]">
            <Segmented
              ariaLabel="Typ-Filter"
              options={TYPE_LABELS}
              value={typeFilter}
              onChange={setTypeFilter}
            />
            <Segmented
              ariaLabel="Status-Filter"
              options={STATUS_LABELS}
              value={statusFilter}
              onChange={setStatusFilter}
            />
          </div>
        )}

        {/* Body */}
        <div class="flex-1 min-h-0 overflow-auto">
          {showEmpty && (
            <p class="px-4 py-8 text-center text-xs text-[color:var(--color-on-surface-variant)]">
              Tippe um Aktionen zu filtern oder zu suchen…
            </p>
          )}

          {showNoMatch && (
            <p class="px-4 py-8 text-center text-xs text-[color:var(--color-on-surface-variant)]">
              Keine Treffer
            </p>
          )}

          {showSearchFailed && (
            <p
              role="status"
              class="px-4 py-8 text-center text-xs text-[color:var(--color-on-surface-variant)]"
            >
              Die Suche ist gerade nicht erreichbar — bitte noch einmal tippen.
            </p>
          )}

          {items.length > 0 && (
            <ul ref={listRef} class="py-1" role="listbox" id="juuplan-cmdk-list">
              {items.map((item, i) => {
                const active = i === highlight;

                // Section header before the first action (index 0).
                const isFirstAction = item.kind === "action" && i === 0;
                // Section header before the first search result.
                const isFirstResult = item.kind === "result" && i === firstResultIdx;

                return (
                  <li
                    key={item.kind === "action" ? `action-${item.action.id}` : `result-${item.result.type}-${item.result.id}`}
                    data-idx={i}
                  >
                    {isFirstAction && (
                      <SectionHeader label="Aktionen" />
                    )}
                    {isFirstResult && (
                      <SectionHeader label="Suchergebnisse" />
                    )}
                    {item.kind === "action" ? (
                      <ActionRow
                        action={item.action}
                        active={active}
                        id={`juuplan-cmdk-opt-${i}`}
                        onHover={() => setHighlight(i)}
                        onClick={() => item.action.run()}
                      />
                    ) : (
                      <ResultRow
                        result={item.result}
                        active={active}
                        id={`juuplan-cmdk-opt-${i}`}
                        onHover={() => setHighlight(i)}
                        onClick={() => navigateToResult(item.result)}
                      />
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Footer hint */}
        <div class="px-4 py-2 border-t border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-low)] text-label text-[color:var(--color-on-surface-variant)] flex items-center justify-between">
          <span>
            <kbd class="px-1 rounded border border-[color:var(--color-outline-variant)]">↑</kbd>
            <kbd class="ml-1 px-1 rounded border border-[color:var(--color-outline-variant)]">↓</kbd>
            <span class="ml-1">Navigieren</span>
            <kbd class="ml-3 px-1 rounded border border-[color:var(--color-outline-variant)]">Enter</kbd>
            <span class="ml-1">Ausführen</span>
            <kbd class="ml-3 px-1 rounded border border-[color:var(--color-outline-variant)]">Esc</kbd>
            <span class="ml-1">Schließen</span>
          </span>
        </div>
      </div>
    </div>
  );

  return createPortal(node, document.body);
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/** Compact single-select segmented control used for the search filter chips. */
function Segmented<T extends string>({
  ariaLabel,
  options,
  value,
  onChange,
}: {
  ariaLabel: string;
  options: { value: T; label: string }[];
  value: T;
  onChange: (v: T) => void;
}): preact.JSX.Element {
  return (
    <div class="inline-flex items-center gap-0.5" role="group" aria-label={ariaLabel}>
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            onClick={() => onChange(o.value)}
            aria-pressed={active}
            class={`rounded-full px-2.5 py-0.5 text-micro font-medium transition-colors ${
              active
                ? "bg-[color:var(--color-primary)] text-[color:var(--color-on-primary)]"
                : "text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container-high)]"
            }`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function SectionHeader({ label }: { label: string }): preact.JSX.Element {
  return (
    <div class="px-4 pt-2 pb-1 text-label font-semibold uppercase tracking-wider text-[color:var(--color-on-surface-variant)]">
      {label}
    </div>
  );
}

function ActionRow({
  action,
  active,
  id,
  onHover,
  onClick,
}: {
  action: PaletteAction;
  active: boolean;
  id?: string;
  onHover: () => void;
  onClick: () => void;
}): preact.JSX.Element {
  const Icon = action.icon;
  return (
    <button
      type="button"
      id={id}
      onMouseEnter={onHover}
      onClick={onClick}
      class={`w-full text-left flex items-center gap-3 px-4 py-2 transition-colors ${
        active
          ? "bg-[color:var(--color-primary)]/10"
          : "hover:bg-[color:var(--color-surface-container-low)]"
      }`}
      role="option"
      aria-selected={active}
    >
      <Icon
        size={16}
        class="shrink-0 text-[color:var(--color-on-surface-variant)]"
        aria-hidden="true"
      />
      <span class="flex-1 min-w-0 text-sm text-[color:var(--color-on-surface)] truncate">
        {action.label}
      </span>
    </button>
  );
}

function ResultRow({
  result,
  active,
  id,
  onHover,
  onClick,
}: {
  result: SearchResult;
  active: boolean;
  id?: string;
  onHover: () => void;
  onClick: () => void;
}): preact.JSX.Element {
  const Icon = RESULT_ICON[result.type];
  return (
    <button
      type="button"
      id={id}
      onMouseEnter={onHover}
      onClick={onClick}
      class={`w-full text-left flex items-center gap-3 px-4 py-2 transition-colors ${
        active
          ? "bg-[color:var(--color-primary)]/10"
          : "hover:bg-[color:var(--color-surface-container-low)]"
      }`}
      role="option"
      aria-selected={active}
    >
      <Icon
        size={16}
        class="shrink-0 text-[color:var(--color-on-surface-variant)]"
        aria-hidden="true"
      />
      <span class="flex-1 min-w-0">
        <span class="block text-sm text-[color:var(--color-on-surface)] truncate">
          {result.title}
        </span>
        {(result.subtitle || result.matched_via) && (
          <span class="block text-micro text-[color:var(--color-on-surface-variant)] truncate">
            {result.subtitle}
            {result.matched_via && (
              <span class="italic">
                {result.subtitle ? " · " : ""}
                {result.matched_via === "comment" ? "Kommentar" : "Notiz"}
              </span>
            )}
          </span>
        )}
      </span>
      {result.color && (
        <span
          class="size-2 rounded-full shrink-0"
          style={{ background: result.color }}
          aria-hidden="true"
        />
      )}
      <span class="text-label uppercase tracking-wider text-[color:var(--color-on-surface-variant)] shrink-0">
        {RESULT_LABEL[result.type]}
      </span>
    </button>
  );
}

const RESULT_ICON: Record<SearchResultType, LucideIcon> = {
  project: Folder,
  task: CheckSquare,
  list: ListChecks,
  personal_task: ListTodo,
};

const RESULT_LABEL: Record<SearchResultType, string> = {
  project: "Projekt",
  task: "Aufgabe",
  list: "Liste",
  personal_task: "Aufgabe",
};
